/**
 * Undo/Redo MVI Effects
 *
 * Architecture: MVI Pattern - Effects Layer
 * Side effects: API calls, sessionStorage, keyboard listeners.
 */

import {
  getChangelogApiClient,
  getSourcesApiClient,
  setMutationCallback,
  clearMutationCallback,
  toApiError,
} from "@/shared/api";
import type { IUndoRedoResult } from "@/shared/api";
import { PERMISSION_PROJECT_EDIT } from "@/shared/types";
import type { TUndoRedoIntent } from "./intents";

// ============================================================================
// Session Effects
// ============================================================================

const SESSION_STORAGE_KEY = "editSessionClientId";
const HEARTBEAT_INTERVAL_MS = 30_000;

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let visibilityChangeHandler: (() => void) | null = null;

/**
 * Есть ли у пользователя право редактирования проекта.
 *
 * Отказ чтения прав приравнивается к их отсутствию: сеанс не открывается, но и
 * отказом в доступе выбор проекта не оборачивается — наблюдатель просто
 * работает без кнопок отмены (edit-sessions-R03).
 */
async function mayEditProject(projectId: number): Promise<boolean> {
  try {
    const result = await getSourcesApiClient().getMyPermissions(String(projectId));
    return (result.permissionsBitmask & PERMISSION_PROJECT_EDIT) !== 0;
  } catch {
    return false;
  }
}

/**
 * Initialize edit session for this browser tab.
 * Generates a client UUID, stores it in sessionStorage, and calls the API.
 *
 * Сеанс открывается **только** при праве редактирования выбранного проекта:
 * актору без него — наблюдателю — сеанс не запрашивается вовсе, потому что
 * редактировать он не будет, и отказ в доступе на открытие сеанса штатным
 * исходом выбора проекта не является (edit-sessions-R03, R11).
 */
export async function initSession(
  projectId: number,
  dispatch: (intent: TUndoRedoIntent) => void,
): Promise<void> {
  dispatch({ type: "SessionInitStarted" });

  if (!(await mayEditProject(projectId))) {
    dispatch({ type: "SessionInitFailed" });
    return;
  }

  try {
    // Reuse existing client ID from sessionStorage (same tab after refresh)
    let clientId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (clientId === null || clientId.length === 0) {
      clientId = crypto.randomUUID();
      sessionStorage.setItem(SESSION_STORAGE_KEY, clientId);
    }

    const api = getChangelogApiClient();
    const session = await api.createEditSession({ clientId, projectId });

    dispatch({
      type: "SessionInitCompleted",
      sessionId: session.id,
      clientId,
      // Сеанс принадлежит проекту, в котором открыт (edit-sessions-R04):
      // кнопки отмены даёт только он (undo-redo-R11).
      projectId,
      canUndo: session.canUndo,
      canRedo: session.canRedo,
    });

    // After any mutation, re-read the session journal instead of assuming the
    // change produced an undoable action: a change that was not journalled must
    // not light up the undo button, or pressing it would undo the *previous*
    // group rather than what the user just did (undo-redo-R14, R10).
    setMutationCallback(() => {
      void refreshUndoRedoState(dispatch);
    });

    // Start heartbeat
    startHeartbeat();
  } catch (error) {
    console.error("Failed to init edit session:", error);
    dispatch({ type: "SessionInitFailed" });
  }
}

/**
 * Re-read actual undo/redo availability from the session journal.
 *
 * A failed read leaves the previous state untouched: guessing here is exactly
 * what produced the false "Undo" activation.
 */
export async function refreshUndoRedoState(
  dispatch: (intent: TUndoRedoIntent) => void,
): Promise<void> {
  try {
    const api = getChangelogApiClient();
    const state = await api.getUndoRedoState();
    dispatch({ type: "StateUpdated", canUndo: state.canUndo, canRedo: state.canRedo });
  } catch {
    // Keep the last known state — an unreachable server is not evidence that
    // something became undoable.
  }
}

/**
 * Close the current edit session.
 * Uses sendBeacon for reliable delivery on page unload.
 */
export async function closeSession(
  dispatch: (intent: TUndoRedoIntent) => void,
): Promise<void> {
  stopHeartbeat();
  clearMutationCallback();

  try {
    const api = getChangelogApiClient();
    await api.closeEditSession();
  } catch {
    // Best effort — session will expire via cleanup
  }

  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  dispatch({ type: "SessionClosed" });
}

function startHeartbeat(): void {
  stopHeartbeat();
  const api = getChangelogApiClient();

  const pingIfVisible = (): void => {
    if (document.visibilityState !== "visible") return;
    api.pingEditSession().catch(() => {
      // Heartbeat failure is not critical
    });
  };

  // Immediate heartbeat on tab focus/visibility restore to avoid stale sessions.
  visibilityChangeHandler = (): void => {
    if (document.visibilityState === "visible") {
      pingIfVisible();
    }
  };
  document.addEventListener("visibilitychange", visibilityChangeHandler);

  heartbeatTimer = setInterval(() => {
    pingIfVisible();
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat(): void {
  if (heartbeatTimer !== null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }

  if (visibilityChangeHandler !== null) {
    document.removeEventListener("visibilitychange", visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
}

// ============================================================================
// Undo/Redo Effects
// ============================================================================

/**
 * Execute undo and dispatch result.
 * Returns affected entity kinds for selective refetch.
 *
 * Отказ не гасится: причина, присланная продуктом, доходит до состояния как
 * есть и оттуда — до пользователя (undo-redo-R19, api-errors-R06/R08).
 */
export async function executeUndo(
  dispatch: (intent: TUndoRedoIntent) => void,
): Promise<IUndoRedoResult | null> {
  dispatch({ type: "UndoStarted" });

  try {
    const api = getChangelogApiClient();
    const result = await api.undo();

    dispatch({
      type: "UndoCompleted",
      canUndo: result.canUndo,
      canRedo: result.canRedo,
      affectedEntityKinds: result.affectedEntityKinds,
    });

    return result;
  } catch (error) {
    dispatch({ type: "UndoFailed", error: toApiError(error) });
    return null;
  }
}

/**
 * Execute redo and dispatch result.
 * Returns affected entity kinds for selective refetch.
 */
export async function executeRedo(
  dispatch: (intent: TUndoRedoIntent) => void,
): Promise<IUndoRedoResult | null> {
  dispatch({ type: "RedoStarted" });

  try {
    const api = getChangelogApiClient();
    const result = await api.redo();

    dispatch({
      type: "RedoCompleted",
      canUndo: result.canUndo,
      canRedo: result.canRedo,
      affectedEntityKinds: result.affectedEntityKinds,
    });

    return result;
  } catch (error) {
    dispatch({ type: "RedoFailed", error: toApiError(error) });
    return null;
  }
}

// ============================================================================
// Keyboard Shortcuts
// ============================================================================

type TKeyboardHandler = (event: KeyboardEvent) => void;

let keyboardHandler: TKeyboardHandler | null = null;

/**
 * Register global Ctrl+Z / Ctrl+Shift+Z keyboard shortcuts.
 *
 * Доступность каждой операции совпадает с доступностью её кнопки
 * (undo-redo-R16): кнопки нет либо она неактивна — соответствующая клавиша не
 * действует и событие не перехватывает. Признаки спрашиваются на каждое
 * нажатие, потому что состояние стеков меняется по ходу работы.
 */
export function registerKeyboardShortcuts(
  onUndo: () => void,
  onRedo: () => void,
  canUndo: () => boolean,
  canRedo: () => boolean,
): void {
  unregisterKeyboardShortcuts();

  keyboardHandler = (event: KeyboardEvent): void => {
    // Skip if focus is in an input/textarea/contenteditable (undo-redo-R15).
    // Целью события бывает и не элемент (сам документ) — у него тега нет,
    // поэтому тип объявлен по факту, а не по обещанию DOM.
    const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
    const tagName = target?.tagName?.toLowerCase();
    if (
      tagName === "input" ||
      tagName === "textarea" ||
      target?.isContentEditable === true
    ) {
      return;
    }

    const isCtrlOrMeta = event.ctrlKey || event.metaKey;
    if (!isCtrlOrMeta) return;

    if (event.key === "z" && !event.shiftKey) {
      if (!canUndo()) return;
      event.preventDefault();
      onUndo();
    } else if (
      (event.key === "z" && event.shiftKey) ||
      (event.key === "y" && !event.shiftKey)
    ) {
      if (!canRedo()) return;
      event.preventDefault();
      onRedo();
    }
  };

  document.addEventListener("keydown", keyboardHandler);
}

/**
 * Unregister global keyboard shortcuts.
 */
export function unregisterKeyboardShortcuts(): void {
  if (keyboardHandler !== null) {
    document.removeEventListener("keydown", keyboardHandler);
    keyboardHandler = null;
  }
}
